import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { toast } from "sonner";
import { Loader2, Heart } from "lucide-react";

export default function Boosters() {
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [form, setForm] = useState({ amount: 25, donor_name: "", email: "", anonymous: false, message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.boosters().then(({ data }) => setCampaigns(data));
  useEffect(() => { load(); }, []);

  const donate = async () => {
    if (!form.donor_name || !form.email || !form.amount) { toast("Fill in your details"); return; }
    setBusy(true);
    try {
      const { data } = await api.donate({ campaign_id: active.id, ...form, amount: Number(form.amount) });
      setReceipt(data.receipt); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="boosters-page" className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-12">
      <Seo
        title="School Prom Boosters & Fundraising"
        description="Support Wisconsin school prom funds. Donate to booster campaigns, get instant receipts, and help make prom magical."
        path="/boosters"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Boosters", path: "/boosters" }])}
      />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Boosters", path: "/boosters" }]} />
      <p className="font-label text-[10px] text-rosemetal mb-2">Give back</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-4">Booster & Community</h1>
      <p className="font-body text-sm text-muted-foreground max-w-xl mb-12">Support your school's prom fund. Every gift helps make the night magical for every student — and sponsors get visibility across the school hub.</p>

      <div className="space-y-6">
        {campaigns.map((c) => (
          <div key={c.id} data-testid={`campaign-${c.id}`} className="border border-border rounded-sm p-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="max-w-xl">
                <h3 className="font-display text-3xl">{c.title}</h3>
                <p className="font-body text-sm text-muted-foreground mt-2">{c.description}</p>
                {c.sponsors?.length > 0 && <p className="font-label text-[9px] text-rosemetal mt-4">Sponsored by {c.sponsors.join(" · ")}</p>}
              </div>
              <button data-testid={`donate-btn-${c.id}`} onClick={() => { setActive(c); setReceipt(null); }} className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm flex items-center gap-2"><Heart className="w-4 h-4" /> Donate</button>
            </div>
            <div className="mt-6 h-2 bg-blush rounded-full overflow-hidden"><div className="h-full bg-rosemetal transition-all" style={{ width: `${Math.min(100, (c.raised / c.goal) * 100)}%` }} /></div>
            <div className="flex justify-between font-body text-sm mt-2"><span>${c.raised} raised</span><span className="text-muted-foreground">Goal ${c.goal}</span></div>
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div data-testid="donate-modal" className="bg-ivory rounded-sm max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            {receipt ? (
              <div data-testid="donation-receipt" className="text-center">
                <h3 className="font-display text-3xl mb-3">Thank you 🌹</h3>
                <p className="font-body text-sm text-muted-foreground mb-4">Your gift of <b>${receipt.amount}</b> to {active.title} is confirmed.</p>
                <div className="bg-champagne/40 rounded-sm p-4 font-label text-[10px]">Receipt No. {receipt.receipt_no}</div>
                <button onClick={() => setActive(null)} className="w-full mt-6 bg-ink text-ivory py-3 font-label text-[10px] rounded-sm">Done</button>
              </div>
            ) : (
              <>
                <h3 className="font-display text-3xl mb-6">Donate to {active.title}</h3>
                <div className="flex gap-2 mb-4">{[25, 50, 100, 250].map((a) => <button key={a} onClick={() => setForm({ ...form, amount: a })} className={`flex-1 py-2 font-body text-sm border rounded-sm ${Number(form.amount) === a ? "bg-ink text-ivory border-ink" : "border-border"}`}>${a}</button>)}</div>
                <input data-testid="donate-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-3 outline-none focus:border-rosemetal" placeholder="Amount" />
                <input data-testid="donor-name" placeholder="Your name" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-3 outline-none focus:border-rosemetal" />
                <input data-testid="donor-email" placeholder="Email (for receipt)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-3 outline-none focus:border-rosemetal" />
                <label className="flex items-center gap-2 font-body text-xs mb-4"><input type="checkbox" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} /> Give anonymously</label>
                <button data-testid="submit-donation" onClick={donate} disabled={busy} className="w-full bg-ink text-ivory py-3.5 font-label text-[10px] rounded-sm flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Complete Gift</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
